import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { createBackoffPoller } from 'modules/common/utils/poll-with-backoff'
import { apiCall } from 'modules/core/services/api'
import type { DayBucketDto } from 'openapi/models/DayBucketDto'
import type { RegenerateCreativeDto } from 'openapi/models/RegenerateCreativeDto'
import type { WeeklyQueueItemDto } from 'openapi/models/WeeklyQueueItemDto'
import type { WeeklyQueueResponseDto } from 'openapi/models/WeeklyQueueResponseDto'
import { CreativeService } from 'openapi/services/CreativeService'
import { WeeklyQueueService } from 'openapi/services/WeeklyQueueService'
import type { InstagramMediaItem } from '../../creative/types'
import type { LivePlatform } from '../../creative/store/idea-store'
import { OpenAPI } from 'openapi/core/OpenAPI'
import { request as __request } from 'openapi/core/request'
import { create } from 'zustand'
import {
  isConnectableChannel,
  toDayKeyIso,
  type ConnectableChannel,
} from '../utils/weekly-queue-utils'

/**
 * Optional selection carried by an approve. When `platforms` is present the
 * server runs the full confirm contract inside the approval (selected previews
 * become scheduled siblings, `confirmedPlatforms` is written) instead of only
 * stamping `approvedAt`. Both fields absent = the quick-approve.
 */
export interface ApprovePostBody {
  platforms?: LivePlatform[]
  scheduledAt?: string
}

/**
 * Approve a review-mode post for publishing. Hand-rolled (not via the generated
 * `WeeklyQueueService`) because the OpenAPI client lags server route additions
 * during Tilt rebuilds. Always sends a JSON body (`{}` for the quick-approve)
 * so the server's optional DTO validates the same way on every path.
 */
function approveCreativeApi(
  creativeId: string,
  body: ApprovePostBody = {}
): Promise<WeeklyQueueItemDto> {
  return __request(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{id}/approve',
    path: { id: creativeId },
    body,
    mediaType: 'application/json',
  })
}

/**
 * Unschedule a post (clears `scheduledAt` → back to a draft). Shared
 * composer/calendar endpoint, which emits the POST_UNSCHEDULED event
 * server-side. Hand-rolled to match `approveCreativeApi` (the OpenAPI client
 * lags route additions during Tilt rebuilds).
 */
function unscheduleCreativeApi(creativeId: string): Promise<void> {
  return __request(OpenAPI, {
    method: 'PATCH',
    url: '/v1/calendar/entries/{creativeId}/unschedule',
    path: { creativeId },
  })
}

/**
 * Extended item DTO with cockpit-cutover fields. The OpenAPI generator
 * lags behind server changes during Tilt rebuild cycles, so we define the
 * new fields here as optional. Once the openapi-watcher catches up,
 * `WeeklyQueueItemDto` itself will carry them and this alias collapses.
 */
export type PlatformPublishStatus = 'pending' | 'published' | 'failed'

export interface PlatformStatus {
  /** Sibling Creative ID — used to route per-sibling Pusher events to the right pill. */
  creativeId: string
  /** LivePlatform / CreativeEntityType identifier (e.g. "instagram"). */
  platform: string
  status: PlatformPublishStatus
  /** When the sibling published (ISO 8601). */
  publishedAt?: string
  /** Public permalink to the live post on this platform (only when published). */
  permalink?: string
  /**
   * Human-readable failure reason from the publish processor. This field is
   * overloaded — it carries a bare token (e.g. `token_expired`) for stamped
   * markers and a full sentence for restriction reasons; the failure UI must
   * NOT parse it. Use `failureReason` for the machine token; keep
   * `errorMessage` for the restriction sentence fallback only.
   */
  errorMessage?: string
  /**
   * Machine failure token from the server classifier (see
   * `apps/server/src/monitoring/utils/publish-monitoring.ts WARNING_REASONS`).
   * Feed to `getPublishFailureCopy` for the humanized card copy — never
   * render raw. Absent when the sibling didn't fail (or when the server DTO
   * hasn't caught up yet).
   */
  failureReason?: string
  /** When the sibling was marked failed (ISO 8601). */
  failedAt?: string
}

/**
 * Cockpit lifecycle status. Derived server-side from the creative's
 * scheduledAt / publishedAt / generationStatus:
 *   - `generating` — content still being drafted
 *   - `needs_approval` — ready on a review-mode plan, awaiting user approval
 *   - `scheduled` — on the publish rails, not yet live
 *   - `published` — live
 *   - `failed` — generation or publish failed
 *
 * Declared locally (not from `WeeklyQueueItemDto['status']`) because the
 * OpenAPI generator lags the server cutover during Tilt rebuild cycles; once
 * the generated DTO carries the new enum this alias can switch back to it.
 */
export type WeeklyQueueStatus =
  'generating' | 'needs_approval' | 'scheduled' | 'published' | 'failed'

export type WeeklyQueueItem = Omit<WeeklyQueueItemDto, 'platformStatuses' | 'status'> & {
  /** Derived lifecycle status (new enum — overrides the lagging generated one). */
  status: WeeklyQueueStatus
  /**
   * Server-computed: the composition carries edits the flat master hasn't
   * re-rendered yet, so the thumbnail lags the user's design. (Declared here
   * until the next openapi regen picks it up from WeeklyQueueItemDto.)
   */
  editsApplying?: boolean
  /**
   * Server-computed: this slot's AI generation terminally failed (no content
   * was produced) — as opposed to a publish failure, which also rolls up to
   * status='failed'. Drives the failed-media plate + Retry on the card.
   * (Declared here until the next openapi regen picks it up from the DTO.)
   */
  generationFailed?: boolean
  /** "post_project" = new social-post slot, "legacy_creative" = pre-cutover single-platform draft. */
  kind?: 'post_project' | 'legacy_creative'
  /** Wrapping Project ID (only when kind=post_project). */
  projectId?: string
  /** ContentShape (only when kind=post_project; cockpit always emits 'static_image'). */
  shape?: string
  /** Default platform the agent generated for (only when kind=post_project). */
  primaryPlatform?: string
  /** Platforms the user can fan out to from the Edit modal (only when kind=post_project). */
  candidatePlatforms?: string[]
  /**
   * Platforms this slot will actually publish to. Empty/absent until the user
   * edits the post in PostModal — fall back to `[primaryPlatform]` (a
   * born-scheduled autopilot slot publishes only the primary by default).
   */
  confirmedPlatforms?: string[]
  /**
   * Per-platform publish state for the card's pill row. One entry per sibling
   * Creative (incl. primary) on a Post-project slot. Absent for legacy_creative.
   */
  platformStatuses?: PlatformStatus[]
  /** Representative published platform's public permalink (for the "Live" affordance). */
  permalink?: string
  /** True when on a review-mode plan and awaiting approval (paired with status='needs_approval'). */
  needsApproval?: boolean
  /**
   * Reel source video URL (only for `shape==='short_vertical_video'`). Lets the
   * cockpit card autoplay the reel inline via AssetMedia, matching IdeaCard.
   * Absent until the server DTO carries it — the card falls back to the
   * thumbnail when missing.
   */
  videoUrl?: string | null
  /**
   * Carousel slides (only for `shape==='carousel'`). Lets the cockpit card
   * render the swipeable carousel inline via AssetMedia. Absent until the
   * server DTO carries it — the card falls back to the thumbnail when missing.
   */
  mediaItems?: InstagramMediaItem[]
}
export type WeeklyDayBucket = DayBucketDto

/**
 * Cockpit-cutover response shape. Overrides `items`/`status` with the new enum
 * and swaps the legacy pending/approved/rejected/skipped counts for
 * scheduled/generating/published. Declared locally for the same reason as
 * `WeeklyQueueStatus` — the generated `WeeklyQueueResponseDto` lags the server
 * cutover; Phase G's OpenAPI regen collapses this back into the generated type.
 */
export type WeeklyQueueResponse = Omit<
  WeeklyQueueResponseDto,
  'items' | 'pendingCount' | 'approvedCount' | 'rejectedCount' | 'skippedCount' | 'publishedCount'
> & {
  items: WeeklyQueueItem[]
  scheduledCount: number
  generatingCount: number
  publishedCount: number
  pendingApprovalCount: number
  /** Active plan's publish mode — drives the cockpit mode badge. */
  publishMode?: 'review' | 'automatic'
}

export type WeeklyQueuePendingAction =
  'delete' | 'regenerate' | 'reschedule' | 'approve' | 'unschedule'

interface WeeklyQueueState {
  items: WeeklyQueueItem[]
  days: WeeklyDayBucket[]
  selectedDate: string | null
  windowStart: string | null
  windowEnd: string | null
  /** @deprecated mirrors windowStart for legacy consumers. */
  weekStart: string | null
  /** @deprecated mirrors windowEnd for legacy consumers. */
  weekEnd: string | null
  hasActivePlan: boolean
  /** Active plan's publish mode (drives the cockpit mode badge). */
  publishMode?: 'review' | 'automatic'
  scheduledCount: number
  generatingCount: number
  publishedCount: number
  pendingApprovalCount: number
  /** Business has scheduled posts but no active subscription — sweep holds all publishing. */
  billingHold: boolean
  loading: boolean
  loaded: boolean
  pendingActions: Record<string, WeeklyQueuePendingAction>
  /** Per-day client-side draft placeholders driven by "Add a post" clicks. */
  optimisticDrafts: Record<string, number>
  /**
   * Creative IDs that JUST flipped `generating → done` on the latest poll.
   * One-shot: each entry self-clears after the settle animation window so the
   * cockpit can play the platform-icon center→corner settle exactly once (the
   * card remounts on completion in review mode, so it can't rely on an in-place
   * CSS transition).
   */
  justGeneratedIds: Record<string, true>
  sessionActions: number
  sessionStartedAt: number | null

  fetchQueue: (anchorDate?: string) => Promise<void>
  selectDay: (date: string) => void
  addPostForDay: (date: string) => Promise<void>
  /**
   * Cockpit "Delete" — hard-deletes the whole Post via DELETE /v1/creatives/:id
   * (`CreativeService.delete`), which removes the parent Project + all platform
   * siblings via the FK cascade AND tombstones the autopilot slot so neither
   * backfill (horizon cron + cockpit placeholder) refills the day. The item is
   * removed from `state.items` outright — there is no Undo (a hard delete can't
   * be restored), so callers gate it behind a confirm dialog.
   */
  deletePost: (creativeId: string) => Promise<void>
  /**
   * Cockpit "Unschedule" — clears `scheduledAt` via the shared calendar endpoint
   * so the post becomes a draft (still listed under /posts, just no longer on the
   * publish rails). Unlike delete this keeps the Post; the item simply leaves the
   * week view (it's no longer scheduled). Optimistically removed from `items` +
   * detached from its day; reverts on failure.
   */
  unschedule: (creativeId: string) => Promise<void>
  reschedule: (creativeId: string, scheduledAt: string) => Promise<WeeklyQueueItem | null>
  /**
   * Approve a single review-mode post for publishing. Optimistically flips the
   * card from `needs_approval` → `scheduled`; reverts on failure.
   */
  /**
   * Approve a review-mode post. With `body.platforms` the approval carries the
   * user's platform selection + slot (PostModal); without it, it's the
   * quick-approve that publishes whatever the post already confirmed.
   */
  approvePost: (creativeId: string, body?: ApprovePostBody) => Promise<void>
  regenerate: (
    creativeId: string,
    overrides?: {
      contentDirection?: RegenerateCreativeDto['contentDirection']
    }
  ) => Promise<void>
  /**
   * Optimistically flip a queue item to `published` in response to the
   * `creative:publish:complete` Pusher event. Drives the QueueCard's acid
   * pulse the moment the post actually ships, instead of waiting for the
   * next queue refetch to catch up.
   */
  markItemPublished: (creativeId: string) => void
  /**
   * Optimistically flip a queue item (and/or matching sibling pill) to
   * `failed` in response to the `creative:publish:complete` Pusher event
   * with `success:false`. Mirrors `markItemPublished`'s two routing paths:
   * (1) top-level item — legacy single-platform card OR primary sibling on
   * a Post-project card; the card's top-level status flips to `failed`;
   * (2) sibling pill match — patch that pill's status/failureReason and
   * leave the card's top-level status alone (a multi-platform card can
   * have a failed IG pill next to a live FB pill; the top-level status is
   * left to the next queue refetch to promote — the pill drives the
   * visible failure treatment). No cross-pill "all failed" promotion
   * because partial failure is a valid terminal state per the plan.
   */
  markItemPublishFailed: (creativeId: string, failureReason?: string) => void
  markSessionStart: () => void
  resetSession: () => void
  /**
   * Cancel any in-flight poll-refetch backoff. The cockpit calls this on
   * unmount so the bounded ladder (up to 40s) doesn't fire HTTP requests
   * after the user navigates away.
   */
  cancelPolling: () => void
}

/**
 * Return `pendingActions` with `creativeId` removed. Setting the key to
 * `undefined` would leave it on the object forever, growing the map for the
 * lifetime of the session as different creatives go through transitions.
 */
function clearPendingAction(
  prev: Record<string, WeeklyQueuePendingAction>,
  creativeId: string
): Record<string, WeeklyQueuePendingAction> {
  if (!(creativeId in prev)) return prev
  const next = { ...prev }
  delete next[creativeId]
  return next
}

const queuePoller = createBackoffPoller([2_000, 5_000, 10_000, 20_000, 40_000])

// Optimistic drafts are decoupled from the server's expectedCount on purpose:
// a stalled/failed autopilot generation must never leave a forever-stuck
// "Drafting" card on the day detail.
const OPTIMISTIC_DRAFT_TTL_MS = 90_000
const optimisticDraftTimers = new Map<string, Set<ReturnType<typeof setTimeout>>>()

// How long a `justGenerated` flag lives — long enough for the card's settle
// animation (~0.55s) to mount and play, then cleared so it can't replay on a
// later re-render/remount (scroll, refetch).
const JUST_GENERATED_TTL_MS = 1_500
const justGeneratedTimers = new Set<ReturnType<typeof setTimeout>>()

function clearOptimisticTimer(date: string, timer: ReturnType<typeof setTimeout>) {
  clearTimeout(timer)
  const set = optimisticDraftTimers.get(date)
  if (!set) return
  set.delete(timer)
  if (set.size === 0) optimisticDraftTimers.delete(date)
}

function clearOptimisticTimersForDate(date: string) {
  const set = optimisticDraftTimers.get(date)
  if (!set) return
  for (const id of set) clearTimeout(id)
  optimisticDraftTimers.delete(date)
}

function bumpOptimisticDraft(
  set: (updater: (s: WeeklyQueueState) => Partial<WeeklyQueueState>) => void,
  date: string,
  delta: number
) {
  set(state => {
    const next = Math.max(0, (state.optimisticDrafts[date] ?? 0) + delta)
    const copy = { ...state.optimisticDrafts }
    if (next === 0) delete copy[date]
    else copy[date] = next
    return { optimisticDrafts: copy }
  })
}

/**
 * Schedule a TTL expiry decrement and return a disposer the caller must invoke
 * if the request resolves before the TTL (success or error), so we never
 * double-decrement.
 */
function scheduleOptimisticDraftExpiry(
  set: (updater: (s: WeeklyQueueState) => Partial<WeeklyQueueState>) => void,
  date: string
): () => void {
  const timer = setTimeout(() => {
    bumpOptimisticDraft(set, date, -1)
    const dateSet = optimisticDraftTimers.get(date)
    dateSet?.delete(timer)
    if (dateSet && dateSet.size === 0) optimisticDraftTimers.delete(date)
  }, OPTIMISTIC_DRAFT_TTL_MS)
  const dateSet = optimisticDraftTimers.get(date) ?? new Set()
  dateSet.add(timer)
  optimisticDraftTimers.set(date, dateSet)
  return () => clearOptimisticTimer(date, timer)
}

/** Drop one optimistic slot per real creative that just arrived. */
function reconcileOptimisticDrafts(
  prev: WeeklyDayBucket[],
  next: WeeklyDayBucket[],
  optimisticDrafts: Record<string, number>
): Record<string, number> {
  if (Object.keys(optimisticDrafts).length === 0) return optimisticDrafts
  const prevByDate = new Map(prev.map(d => [d.date, d.itemIds.length]))
  let result = optimisticDrafts
  for (const day of next) {
    const grew = day.itemIds.length - (prevByDate.get(day.date) ?? 0)
    if (grew <= 0) continue
    const current = result[day.date] ?? 0
    if (current === 0) continue
    if (result === optimisticDrafts) result = { ...optimisticDrafts }
    const remaining = current - Math.min(grew, current)
    if (remaining === 0) {
      delete result[day.date]
      clearOptimisticTimersForDate(day.date)
    } else {
      result[day.date] = remaining
    }
  }
  return result
}

/**
 * Remove `creativeId` from every bucket and decrement `expectedCount` by one
 * (clamped to `itemIds.length`) so the day doesn't keep a "Drafting"
 * placeholder for the slot we just emptied.
 */
function detachFromDays(days: WeeklyDayBucket[], creativeId: string): WeeklyDayBucket[] {
  return days.map(d => {
    if (!d.itemIds.includes(creativeId)) return d
    const itemIds = d.itemIds.filter(id => id !== creativeId)
    const expectedCount = Math.max(itemIds.length, d.expectedCount - 1)
    return { ...d, itemIds, expectedCount }
  })
}

/** Insert `creativeId` into `dayKey`'s bucket, sorted by scheduledAt asc. */
function attachToDay(
  days: WeeklyDayBucket[],
  items: WeeklyQueueItem[],
  dayKey: string,
  creativeId: string
): WeeklyDayBucket[] {
  const target = days.find(d => d.date === dayKey)
  if (!target || target.itemIds.includes(creativeId)) return days
  const byId = new Map(items.map(i => [i.creativeId, i]))
  const inserted = [...target.itemIds, creativeId].sort((a, b) => {
    const sa = byId.get(a)?.scheduledAt ?? ''
    const sb = byId.get(b)?.scheduledAt ?? ''
    return sa.localeCompare(sb)
  })
  return days.map(d =>
    d.date === dayKey
      ? { ...d, itemIds: inserted, expectedCount: Math.max(d.expectedCount, inserted.length) }
      : d
  )
}

/**
 * De-dup a server response's `items` so each Post-project (`projectId`)
 * surfaces at most once. Defensive against any future server regression
 * where siblings get re-fanned-out as standalone items — the cockpit's
 * design contract is one card per Post.
 *
 * Tie-breaking: prefer the entry whose `creativeId === projectId`'s primary
 * (we don't have the project's primaryCreativeId on the client, so we
 * approximate by keeping the entry that already carries `platformStatuses`
 * — that's the collapsed item the server emitted; siblings would have
 * `kind === 'post_project'` but no `platformStatuses` set). Falls back to
 * the first occurrence.
 *
 * Legacy items (no projectId) are keyed by creativeId so they never
 * coalesce with anything else.
 */
function dedupItemsByProject(items: WeeklyQueueItem[]): WeeklyQueueItem[] {
  const byKey = new Map<string, WeeklyQueueItem>()
  for (const it of items) {
    const key = it.projectId ?? `legacy:${it.creativeId}`
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, it)
      continue
    }
    // Prefer the entry that already carries platformStatuses — that's the
    // server-collapsed representative.
    const existingHasPills = (existing.platformStatuses?.length ?? 0) > 0
    const incomingHasPills = (it.platformStatuses?.length ?? 0) > 0
    if (incomingHasPills && !existingHasPills) byKey.set(key, it)
  }
  return Array.from(byKey.values())
}

/**
 * Pick a sensible default day for the strip:
 *   1. First day with an in-flight (generating) item
 *   2. Today if today has any item
 *   3. First day that has any item
 *   4. Today (always fall back to something so the strip has a selection)
 */
function pickDefaultSelectedDate(days: WeeklyDayBucket[], items: WeeklyQueueItem[]): string | null {
  if (days.length === 0) return null

  const itemsById = new Map(items.map(item => [item.creativeId, item]))
  const today = days[0]?.date ?? null

  const firstGenerating = days.find(day =>
    day.itemIds.some(id => itemsById.get(id)?.status === 'generating')
  )
  if (firstGenerating) return firstGenerating.date

  if (today) {
    const todayBucket = days.find(d => d.date === today)
    if (todayBucket && todayBucket.itemIds.length > 0) return today
  }

  const firstWithItems = days.find(d => d.itemIds.length > 0)
  if (firstWithItems) return firstWithItems.date

  return today
}

/** True when the server still has work in flight for this window. */
function hasOutstandingGeneration(response: WeeklyQueueResponse): boolean {
  for (const item of response.items) {
    if (item.status === 'generating') return true
  }
  for (const day of response.days) {
    if (day.expectedCount > day.itemIds.length) return true
  }
  return false
}

function applyResponse(
  set: (s: Partial<WeeklyQueueState>) => void,
  get: () => WeeklyQueueState,
  response: WeeklyQueueResponse
) {
  const prevState = get()
  const prevSelected = prevState.selectedDate
  const selectedStillValid = !!prevSelected && response.days.some(day => day.date === prevSelected)
  const nextSelected = selectedStillValid
    ? prevSelected
    : pickDefaultSelectedDate(response.days, response.items)

  const windowStart = response.windowStart ?? response.weekStart
  const windowEnd = response.windowEnd ?? response.weekEnd

  const optimisticDrafts = reconcileOptimisticDrafts(
    prevState.days,
    response.days,
    prevState.optimisticDrafts
  )

  // Defensive de-dup by projectId. The server already collapses siblings into
  // one item per Post-project (see WeeklyQueueService.getWeeklyQueue), but
  // this is cheap insurance against any future server regression that re-fans
  // out siblings as standalone items. Legacy items (no projectId) are keyed
  // by creativeId so they never coalesce with anything else.
  const items = dedupItemsByProject(response.items as WeeklyQueueItem[])

  // Flag posts that flipped `generating → done` since the last poll so the
  // cockpit can play the platform-icon settle once. Self-clears via TTL below.
  const prevStatusById = new Map(prevState.items.map(i => [i.creativeId, i.status]))
  const newlyFinished = items
    .filter(it => prevStatusById.get(it.creativeId) === 'generating' && it.status !== 'generating')
    .map(it => it.creativeId)
  let justGeneratedIds = prevState.justGeneratedIds
  if (newlyFinished.length > 0) {
    justGeneratedIds = { ...justGeneratedIds }
    for (const id of newlyFinished) justGeneratedIds[id] = true
  }

  set({
    items,
    days: response.days,
    selectedDate: nextSelected,
    windowStart,
    windowEnd,
    weekStart: windowStart,
    weekEnd: windowEnd,
    hasActivePlan: response.hasActivePlan,
    publishMode: response.publishMode,
    scheduledCount: response.scheduledCount,
    generatingCount: response.generatingCount,
    publishedCount: response.publishedCount,
    pendingApprovalCount: response.pendingApprovalCount ?? 0,
    billingHold: response.billingHold === true,
    optimisticDrafts,
    justGeneratedIds,
    loaded: true,
  })

  // Clear each one-shot flag after the settle window so it can't replay on a
  // later re-render or remount of the same card.
  if (newlyFinished.length > 0) {
    const timer = setTimeout(() => {
      justGeneratedTimers.delete(timer)
      const current = get().justGeneratedIds
      let changed = false
      const next = { ...current }
      for (const id of newlyFinished) {
        if (id in next) {
          delete next[id]
          changed = true
        }
      }
      if (changed) set({ justGeneratedIds: next })
    }, JUST_GENERATED_TTL_MS)
    justGeneratedTimers.add(timer)
  }

  // Keep polling until the server has nothing left to generate.
  if (hasOutstandingGeneration(response)) {
    queuePoller.schedule(() => get().fetchQueue())
  } else {
    queuePoller.cancel()
  }
}

function recomputeCounts(items: WeeklyQueueItem[]) {
  const counts = {
    scheduledCount: 0,
    generatingCount: 0,
    publishedCount: 0,
    pendingApprovalCount: 0,
  }
  for (const item of items) {
    switch (item.status) {
      case 'scheduled':
        counts.scheduledCount += 1
        break
      case 'generating':
        counts.generatingCount += 1
        break
      case 'needs_approval':
        counts.pendingApprovalCount += 1
        break
      case 'published':
        counts.publishedCount += 1
        break
      // 'failed' is intentionally uncounted — it's neither scheduled work nor
      // a win; the card surfaces it inline.
    }
  }
  return counts
}

export const useWeeklyQueueStore = create<WeeklyQueueState>((set, get) => ({
  items: [],
  days: [],
  selectedDate: null,
  windowStart: null,
  windowEnd: null,
  weekStart: null,
  weekEnd: null,
  hasActivePlan: false,
  publishMode: undefined,
  scheduledCount: 0,
  generatingCount: 0,
  publishedCount: 0,
  pendingApprovalCount: 0,
  billingHold: false,
  loading: false,
  loaded: false,
  pendingActions: {},
  optimisticDrafts: {},
  justGeneratedIds: {},
  sessionActions: 0,
  sessionStartedAt: null,

  fetchQueue: async (anchorDate?: string) => {
    if (get().loading) return
    set({ loading: true })
    try {
      const response = await apiCall(
        WeeklyQueueService.weeklyQueueControllerGetWeeklyQueueV1,
        null,
        anchorDate
      )
      // Cast through the cutover shape: the generated DTO still types the old
      // counts + status enum (Phase G regen fixes this).
      if (response) applyResponse(set, get, response as unknown as WeeklyQueueResponse)
    } catch (error) {
      ErrorReporting.captureException(error, {
        tags: { feature: 'weekly_cockpit', step: 'fetch_queue' },
        extra: { anchorDate },
      })
      console.error('weekly-queue fetch failed:', error)
      // Also drop derived alert state — a stale billingHold would keep the
      // amber reactivate strip up over an empty cockpit.
      set({ items: [], days: [], loaded: true, billingHold: false, pendingApprovalCount: 0 })
    } finally {
      set({ loading: false })
    }
  },

  addPostForDay: async (date: string) => {
    bumpOptimisticDraft(set, date, +1)
    // TTL is the safety net for "server accepted but generation never lands"
    // — `reconcileOptimisticDrafts` will normally clear the placeholder when
    // the real creative arrives via polling, and the TTL no-ops via Math.max.
    const cancelExpiry = scheduleOptimisticDraftExpiry(set, date)
    try {
      await apiCall(WeeklyQueueService.weeklyQueueControllerAddPostV1, null, { date })
    } catch (error) {
      cancelExpiry()
      bumpOptimisticDraft(set, date, -1)
      throw error
    }
    queuePoller.schedule(() => get().fetchQueue())
  },

  selectDay: (date: string) => {
    const normalized = toDayKeyIso(date)
    const match = get().days.find(d => d.date === date || d.date === normalized)
    if (!match) return
    set({ selectedDate: match.date })
  },

  deletePost: async (creativeId: string) => {
    set(state => ({ pendingActions: { ...state.pendingActions, [creativeId]: 'delete' } }))
    // Snapshot so we can roll back if the delete call fails.
    const previousState = get()
    // Optimistically remove the post from `items` and detach it from its day
    // bucket (`detachFromDays` decrements `expectedCount`, so no "Drafting…"
    // placeholder pops back in for the slot we just emptied). The server hard-
    // deletes the parent Project + siblings and tombstones the autopilot slot,
    // so the follow-up fetchQueue stays clean.
    set(state => {
      const items = state.items.filter(it => it.creativeId !== creativeId)
      return {
        items,
        days: detachFromDays(state.days, creativeId),
        ...recomputeCounts(items),
      }
    })
    try {
      await apiCall(CreativeService.creativeControllerDeleteV1, null, creativeId)
      set(state => ({
        sessionActions: state.sessionActions + 1,
        pendingActions: clearPendingAction(state.pendingActions, creativeId),
      }))
      // Resync siblings/counts and confirm the slot tombstone landed.
      void get().fetchQueue()
    } catch (error) {
      set(state => ({
        items: previousState.items,
        days: previousState.days,
        ...recomputeCounts(previousState.items),
        pendingActions: clearPendingAction(state.pendingActions, creativeId),
      }))
      throw error
    }
  },

  unschedule: async (creativeId: string) => {
    set(state => ({ pendingActions: { ...state.pendingActions, [creativeId]: 'unschedule' } }))
    // Snapshot so we can roll back if the call fails.
    const previousState = get()
    // Optimistically drop the post from the week view + its day bucket — an
    // unscheduled post is no longer on the calendar. Unlike delete it isn't
    // destroyed; it survives as a draft under /posts.
    set(state => {
      const items = state.items.filter(it => it.creativeId !== creativeId)
      return {
        items,
        days: detachFromDays(state.days, creativeId),
        ...recomputeCounts(items),
      }
    })
    try {
      await apiCall(unscheduleCreativeApi, null, creativeId)
      set(state => ({
        sessionActions: state.sessionActions + 1,
        pendingActions: clearPendingAction(state.pendingActions, creativeId),
      }))
      // Resync counts/buckets with the server.
      void get().fetchQueue()
    } catch (error) {
      set(state => ({
        items: previousState.items,
        days: previousState.days,
        ...recomputeCounts(previousState.items),
        pendingActions: clearPendingAction(state.pendingActions, creativeId),
      }))
      throw error
    }
  },

  reschedule: async (creativeId: string, scheduledAt: string) => {
    const dayKey = toDayKeyIso(scheduledAt)
    const previousState = get()
    const previousItem = previousState.items.find(i => i.creativeId === creativeId)
    set(state => {
      const items = state.items.map(it =>
        it.creativeId === creativeId
          ? {
              ...it,
              scheduledAt,
              // Re-attaching a schedule revives an unscheduled draft back to
              // 'scheduled' (unless it already published).
              status: it.status === 'published' ? it.status : ('scheduled' as const),
            }
          : it
      )
      const detached = detachFromDays(state.days, creativeId)
      const days = attachToDay(detached, items, dayKey, creativeId)
      return {
        items,
        days,
        ...recomputeCounts(items),
        pendingActions: { ...state.pendingActions, [creativeId]: 'reschedule' },
      }
    })

    try {
      const updatedDto = await apiCall(
        WeeklyQueueService.weeklyQueueControllerRescheduleV1,
        null,
        creativeId,
        { scheduledAt }
      )
      if (!updatedDto) return null
      // Generated DTO still carries the old status enum (Phase G regen fixes).
      const updated = updatedDto as unknown as WeeklyQueueItem
      set(state => {
        const items = state.items.map(it => (it.creativeId === creativeId ? updated : it))
        return {
          items,
          ...recomputeCounts(items),
          sessionActions: state.sessionActions + 1,
          pendingActions: clearPendingAction(state.pendingActions, creativeId),
        }
      })
      return updated
    } catch (error) {
      set(state => {
        const items = previousItem
          ? state.items.map(it => (it.creativeId === creativeId ? previousItem : it))
          : state.items
        return {
          items,
          days: previousState.days,
          ...recomputeCounts(items),
          pendingActions: clearPendingAction(state.pendingActions, creativeId),
        }
      })
      throw error
    }
  },

  approvePost: async (creativeId: string, body?: ApprovePostBody) => {
    const previousState = get()
    const previousItem = previousState.items.find(i => i.creativeId === creativeId)
    // Optimistically flip needs_approval → scheduled. A selection-carrying
    // approve also reflects the platforms + slot it commits to, so the card
    // reads honestly until the server DTO replaces it below.
    set(state => {
      const items = state.items.map(it =>
        it.creativeId === creativeId && it.status === 'needs_approval'
          ? {
              ...it,
              status: 'scheduled' as const,
              needsApproval: false,
              ...(body?.platforms && { confirmedPlatforms: body.platforms }),
              ...(body?.scheduledAt && { scheduledAt: body.scheduledAt }),
            }
          : it
      )
      // An approve that moves the slot moves the card between day columns,
      // exactly like reschedule(): the cockpit renders days from `days`.
      const days = body?.scheduledAt
        ? attachToDay(
            detachFromDays(state.days, creativeId),
            items,
            toDayKeyIso(body.scheduledAt),
            creativeId
          )
        : state.days
      return {
        items,
        days,
        ...recomputeCounts(items),
        pendingActions: { ...state.pendingActions, [creativeId]: 'approve' },
      }
    })
    try {
      const updatedDto = await apiCall(approveCreativeApi, null, creativeId, body ?? {})
      set(state => {
        const updated = updatedDto as unknown as WeeklyQueueItem | undefined
        const items = updated
          ? state.items.map(it => (it.creativeId === creativeId ? updated : it))
          : state.items
        // Re-bucket on the server's slot (the truth; the optimistic bucket
        // used the request's).
        const days = updated?.scheduledAt
          ? attachToDay(
              detachFromDays(state.days, creativeId),
              items,
              toDayKeyIso(updated.scheduledAt),
              creativeId
            )
          : state.days
        return {
          items,
          days,
          ...recomputeCounts(items),
          sessionActions: state.sessionActions + 1,
          pendingActions: clearPendingAction(state.pendingActions, creativeId),
        }
      })
    } catch (error) {
      set(state => {
        const items = previousItem
          ? state.items.map(it => (it.creativeId === creativeId ? previousItem : it))
          : state.items
        return {
          items,
          days: previousState.days,
          ...recomputeCounts(items),
          pendingActions: clearPendingAction(state.pendingActions, creativeId),
        }
      })
      throw error
    }
  },

  regenerate: async (creativeId, overrides) => {
    set(state => {
      const items = state.items.map(it =>
        it.creativeId === creativeId
          ? {
              ...it,
              status: 'generating' as const,
            }
          : it
      )
      return {
        items,
        ...recomputeCounts(items),
        pendingActions: { ...state.pendingActions, [creativeId]: 'regenerate' },
      }
    })
    try {
      // Send `{}` rather than `undefined` because the generated client types
      // the body as required (NestJS auto-marks DTO bodies required even when
      // every field is optional).
      const body: RegenerateCreativeDto = {
        ...(overrides?.contentDirection ? { contentDirection: overrides.contentDirection } : null),
      }
      await apiCall(WeeklyQueueService.weeklyQueueControllerRegenerateV1, null, creativeId, body)
      set(state => ({
        sessionActions: state.sessionActions + 1,
        pendingActions: clearPendingAction(state.pendingActions, creativeId),
      }))
    } catch (error) {
      set(state => ({ pendingActions: clearPendingAction(state.pendingActions, creativeId) }))
      throw error
    }
  },

  markItemPublishFailed: (creativeId: string, failureReason?: string) => {
    set(state => {
      // One pass over items — for each we check both routes at once:
      //   isTop    → this card's top-level creativeId matches (legacy single-
      //              platform card OR Post-project card whose primary failed).
      //   pill     → a sibling pill on the card matches (partial fail).
      // Skip when neither hits. For an already-failed target we still let a
      // NEW reason through (e.g. token_expired → account_restricted after an
      // autopilot retry) so the chip refreshes without a refetch; skip only
      // when there's no new info (no reason, or same reason).
      // No "all failed" promotion — partial failure is a valid terminal state
      // per the failure-recovery plan; the sibling pill drives the treatment.
      const failedAt = new Date().toISOString()
      let changed = false
      const items = state.items.map(it => {
        const isTop = it.creativeId === creativeId
        const pill = it.platformStatuses?.find(p => p.creativeId === creativeId)
        if (!isTop && !pill) return it
        const alreadyFailed = isTop ? it.status === 'failed' : pill!.status === 'failed'
        if (alreadyFailed && (!failureReason || pill?.failureReason === failureReason)) return it
        changed = true
        const nextPills = it.platformStatuses?.map(p =>
          p.creativeId === creativeId
            ? {
                ...p,
                status: 'failed' as PlatformPublishStatus,
                failureReason: failureReason ?? p.failureReason,
                failedAt,
              }
            : p
        )
        return {
          ...it,
          ...(isTop && { status: 'failed' as WeeklyQueueStatus }),
          ...(nextPills && { platformStatuses: nextPills }),
        }
      })
      if (!changed) return state
      return { items, ...recomputeCounts(items) }
    })
  },

  markItemPublished: (creativeId: string) => {
    set(state => {
      // Two routing paths:
      //   1. Top-level item match — legacy single-platform creative or a
      //      Post-project's primary publish event. Flip the whole card.
      //   2. Pill match — a sibling publish event on a Post-project card.
      //      Patch the matching pill to `published`; only promote the card's
      //      top-level status to `published` once every pill is published.
      const targetItem = state.items.find(it => it.creativeId === creativeId)
      if (targetItem && targetItem.status === 'published') return state

      let changed = false
      const items = state.items.map(it => {
        if (it.creativeId === creativeId) {
          changed = true
          // Also flip the matching pill so platformStatuses stays consistent
          // with the new top-level status.
          const platformStatuses = it.platformStatuses?.map(p =>
            p.creativeId === creativeId ? { ...p, status: 'published' as PlatformPublishStatus } : p
          )
          return {
            ...it,
            status: 'published' as WeeklyQueueStatus,
            ...(platformStatuses && { platformStatuses }),
          }
        }
        const matchingPill = it.platformStatuses?.find(p => p.creativeId === creativeId)
        if (!matchingPill || matchingPill.status === 'published') return it
        changed = true
        const nextPills = it.platformStatuses!.map(p =>
          p.creativeId === creativeId ? { ...p, status: 'published' as PlatformPublishStatus } : p
        )
        const allPublished = nextPills.every(p => p.status === 'published')
        return {
          ...it,
          platformStatuses: nextPills,
          ...(allPublished && { status: 'published' as WeeklyQueueStatus }),
        }
      })
      if (!changed) return state
      return { items, ...recomputeCounts(items) }
    })
  },

  markSessionStart: () => {
    if (get().sessionStartedAt == null) set({ sessionStartedAt: Date.now() })
  },

  resetSession: () => set({ sessionActions: 0, sessionStartedAt: null }),

  cancelPolling: () => queuePoller.cancel(),
}))

/**
 * Selector: return the queue items scheduled for a given day key. `date`
 * must be the `DayBucketDto.date` ISO string from the current state.
 */
export function selectItemsForDay(
  state: Pick<WeeklyQueueState, 'items' | 'days'>,
  date: string | null
): WeeklyQueueItem[] {
  if (!date) return []
  const day = state.days.find(d => d.date === date)
  if (!day) return []
  const byId = new Map(state.items.map(item => [item.creativeId, item]))
  return (
    day.itemIds
      .map(id => byId.get(id))
      .filter((it): it is WeeklyQueueItem => Boolean(it))
      // Posts awaiting approval ride to the top so their Approve CTA is the first
      // thing in the day's vertical stack; chronological within each group.
      .sort((a, b) => {
        const aNeeds = a.status === 'needs_approval' ? 0 : 1
        const bNeeds = b.status === 'needs_approval' ? 0 : 1
        if (aNeeds !== bNeeds) return aNeeds - bNeeds
        const aT = a.scheduledAt ? Date.parse(a.scheduledAt) : Number.POSITIVE_INFINITY
        const bT = b.scheduledAt ? Date.parse(b.scheduledAt) : Number.POSITIVE_INFINITY
        return aT - bT
      })
  )
}

/**
 * Selector: derive social platforms that have at least one upcoming scheduled
 * post (scheduled / generating) but no OAuth connection.
 *
 * Posts on these channels will silently fail to publish at their scheduled
 * time — the cockpit surfaces them in the connect-platforms row above the
 * day strip so the user can connect before publish time arrives. Sorted by
 * `soonestScheduledAt` ascending so the most urgent platform is first.
 */
export function selectMissingPlatformsThisWeek(
  state: Pick<WeeklyQueueState, 'items'>,
  connectedProviders: string[]
): Array<{ channel: ConnectableChannel; postCount: number; soonestScheduledAt: string | null }> {
  const connected = new Set(connectedProviders)
  const buckets = new Map<
    ConnectableChannel,
    { postCount: number; soonestScheduledAt: string | null }
  >()
  for (const item of state.items) {
    if (
      item.status !== 'scheduled' &&
      item.status !== 'generating' &&
      item.status !== 'needs_approval'
    ) {
      continue
    }
    // After the cockpit-cutover collapse, each item is one Post project that
    // can publish to multiple platforms. `item.channel` only carries the
    // primary, so we must iterate over the FULL fan-out set
    // (`confirmedPlatforms` ∥ `[primaryPlatform]` ∥ `[channel]`) to count
    // every publish target — otherwise a post approved for IG+FB+LI shows
    // only "Connect Instagram" even when FB / LI are also unconnected.
    const targets =
      item.confirmedPlatforms && item.confirmedPlatforms.length > 0
        ? item.confirmedPlatforms
        : item.primaryPlatform
          ? [item.primaryPlatform]
          : [item.channel]
    for (const target of targets) {
      if (!isConnectableChannel(target)) continue
      if (connected.has(target)) continue
      const bucket = buckets.get(target) ?? { postCount: 0, soonestScheduledAt: null }
      bucket.postCount += 1
      if (item.scheduledAt) {
        bucket.soonestScheduledAt =
          bucket.soonestScheduledAt && bucket.soonestScheduledAt <= item.scheduledAt
            ? bucket.soonestScheduledAt
            : item.scheduledAt
      }
      buckets.set(target, bucket)
    }
  }
  return Array.from(buckets.entries())
    .map(([channel, bucket]) => ({ channel, ...bucket }))
    .sort((a, b) => {
      if (a.soonestScheduledAt && b.soonestScheduledAt) {
        return a.soonestScheduledAt.localeCompare(b.soonestScheduledAt)
      }
      if (a.soonestScheduledAt) return -1
      if (b.soonestScheduledAt) return 1
      return 0
    })
}

/**
 * Selector: return `{ date -> count }` of upcoming (scheduled / generating)
 * posts across all days for rendering badges/indicators on the week strip.
 */
export function selectPendingByDay(state: WeeklyQueueState): Record<string, number> {
  const byId = new Map(state.items.map(item => [item.creativeId, item]))
  const out: Record<string, number> = {}
  for (const day of state.days) {
    let count = 0
    for (const id of day.itemIds) {
      const item = byId.get(id)
      if (
        item &&
        (item.status === 'scheduled' ||
          item.status === 'generating' ||
          item.status === 'needs_approval')
      ) {
        count += 1
      }
    }
    out[day.date] = count
  }
  return out
}
